import React, { FC, useEffect } from 'react'
import {
  Checkbox as ChkCheckbox,
  CheckboxProps as ChkCheckboxProps,
  useCheckboxGroup,
  SimpleGrid,
  ResponsiveValue,
  Flex,
} from '@chakra-ui/react'

import { Option } from '../Radio'
import { blockSizes } from '../../theme/components'

export type CheckboxProps = ChkCheckboxProps

export const Checkbox: FC<CheckboxProps> = ({
  children,
  noOfLines: _,
  ...props
}) => {
  return (
    <ChkCheckbox {...props} size={['sm', 'md', 'lg']}>
      {children}
    </ChkCheckbox>
  )
}

export type CheckboxGroupProps = Omit<
  CheckboxProps,
  'value' | 'onChange' | 'size' | 'defaultValue'
> & {
  options: Option[]
  value: string[]
  name: string
  onChange: (v: string[]) => void
  defaultValues?: string[]
  dataCy?: string
  size?: keyof typeof blockSizes
  columns?: ResponsiveValue<number>
  withDeselectKeys?: string[]
}

export const CheckboxGroup: FC<CheckboxGroupProps> = ({
  options = [],
  value = [],
  defaultValues = [],
  onChange,
  dataCy,
  size = 'md',
  columns = 2,
  spacing = 8,
  withDeselectKeys,
}) => {
  const deselectSet = withDeselectKeys ? new Set(withDeselectKeys) : undefined
  const { getCheckboxProps, onChange: onToggle } = useCheckboxGroup({
    value,
    onChange(v) {
      onChange(v as string[])
    },
  })
  const group = getCheckboxProps()
  const onClick = (option: Option) => {
    if (deselectSet?.has(option.value)) {
      if (value.includes(option.value)) {
        onToggle(option.value)
      } else {
        onChange([option.value]) // reset the array to contain only deselect option
      }
      return
    }

    // This condition removes the exclusive 'deselect' option
    // if a normal option is clicked.
    if (deselectSet?.has(value[0])) onChange([option.value])
    else onToggle(option.value)
  }

  // Adds the default values to the value array only once when the component mounts
  useEffect(() => {
    if (defaultValues.length) {
      onChange(defaultValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, defaultValues)

  return (
    <SimpleGrid
      data-cy={dataCy}
      columns={
        typeof columns === 'number' ? [Math.min(columns, 2), columns] : columns
      }
      spacing={spacing}
      {...(group as any)}
      width="100%"
    >
      {options.map((option) => (
        <Flex
          {...blockSizes[size]}
          key={option.name}
          data-checked={value.includes(option.value) ? 'true' : undefined}
          onClick={() => onClick(option)}
          cursor="pointer"
          textStyle="cta"
          max-width="fit-content"
          borderWidth={1}
          borderRadius="md"
          justifyContent="center"
          textAlign="center"
          fontSize="sm"
          alignItems="center"
          data-cy={option.name}
          _invalid={{ color: 'pink', borderColor: 'pink' }}
          _checked={{
            bg: 'softBlack',
            borderColor: 'softBlack',
            color: 'softWhite',
            _hover: {
              bg: 'softerBlack',
              borderColor: 'softerBlack',
              color: 'softWhite',
            },
          }}
          _hover={{ bg: 'white', color: 'softBlack', borderColor: 'white' }}
        >
          <span>{option.name}</span>
        </Flex>
      ))}
    </SimpleGrid>
  )
}
